import dataHelpers from '@/helpers/dataHelpers'
import { formatCardKey, getAggregateName, getCommandName } from '@/ai/utils/helpers'

export const createSystemMessage = (content) => {
  return { role: 'system', content }
}

export const createUserMessage = (content) => {
  return { role: 'user', content }
}

export const createAssistantMessage = (content) => {
  return { role: 'assistant', content }
}

export const debugLogMessages = (messages) => {
  if (!messages || messages.length === 0) {
    console.log('No messages to debug.')
    return
  }
  console.log('--- CHAT LOG DEBUG ---')
  for (const msg of messages) {
    console.log(`> Role: ${msg.role}`)
    console.log(`Content:\n${msg.content}`)
    console.log('----------------------')
  }
}

export const diagramToJson = (eventId, workflow, svgGraph, svgLanes, options) => {
  const excludeCards = options?.excludeCards || false
  let currentStep
  const res = []
  let index = 1

  if (!workflow || !workflow.eventsJson || workflow.eventsJson.length === 0) {
    return { workflow: [], currentStep: undefined }
  }
  if (!svgGraph || Object.keys(svgGraph).length === 0 || !svgLanes || Object.keys(svgLanes).length === 0) {
    return { workflow: [], currentStep: undefined }
  }

  const parentChildEventMap = dataHelpers.createMainParentChildEventMap(workflow.eventsJson)

  const eventArray = []
  const effectiveStartEventId = eventId || workflow.eventsJson[0]?.id

  if (!effectiveStartEventId || !svgGraph[effectiveStartEventId]) {
    // Start event ID effectiveStartEventId not found in svgGraph
    return { workflow: [], currentStep: undefined }
  }

  let tmpEventId = effectiveStartEventId
  const MAX_PARENTS = workflow.eventsJson.length * 2
  let parentCount = 0
  while (svgGraph[tmpEventId]?.parents?.[0] && svgGraph[tmpEventId].parents[0] !== 'start' && parentCount < MAX_PARENTS) {
    eventArray.unshift(tmpEventId) // Add current before moving to parent
    const parentId = svgGraph[tmpEventId].parents[0]
    if (!svgGraph[parentId]) { // Parent not in graph, stop
      // We've added tmpEventId already, which is the furthest we could go up.
      break
    }
    tmpEventId = parentId
    parentCount++
    if (eventArray.includes(tmpEventId)) { // Cycle detection
      eventArray.shift() // Remove the duplicate to break the cycle from this unshift logic
      break
    }
  }
  if (parentCount < MAX_PARENTS && svgGraph[tmpEventId] && !eventArray.includes(tmpEventId)) {
    eventArray.unshift(tmpEventId)
  }

  const currentPathNodeId = eventArray.length > 0 ? eventArray[eventArray.length - 1] : effectiveStartEventId
  if (!eventArray.includes(currentPathNodeId) && svgGraph[currentPathNodeId]) {
    if (!eventArray.includes(currentPathNodeId)) eventArray.push(currentPathNodeId)
  }

  const MAX_CHILDREN = workflow.eventsJson.length * 2
  let childrenCount = 0

  tmpEventId = eventArray.length > 0 ? eventArray[eventArray.length - 1] : effectiveStartEventId
  if (!svgGraph[tmpEventId]) {
    // Cannot determine valid node to start downward traversal
    return { workflow: [], currentStep }
  }

  while (childrenCount < MAX_CHILDREN) {
    let nextEventId = null
    if (parentChildEventMap[tmpEventId] && parentChildEventMap[tmpEventId][0]?.id) {
      nextEventId = parentChildEventMap[tmpEventId][0].id
    } else {
      const nextEventIndex = workflow.eventsJson.findIndex((ev) => ev.parents && ev.parents.includes(tmpEventId))
      if (nextEventIndex !== -1) {
        nextEventId = workflow.eventsJson[nextEventIndex].id
      }
    }

    if (nextEventId && svgGraph[nextEventId]) {
      if (eventArray.includes(nextEventId)) {
        // Cycle detected while traversing children at nextEventId. Breaking.`)
        break
      }
      eventArray.push(nextEventId)
      tmpEventId = nextEventId
    } else {
      break
    }
    childrenCount++
  }

  let previousEvent = null
  const processedEventIds = new Set()

  for (const eventIdInPath of eventArray) {
    if (processedEventIds.has(eventIdInPath) || !svgGraph[eventIdInPath]) {
      continue
    }
    processedEventIds.add(eventIdInPath)

    const event = svgGraph[eventIdInPath]
    const cards = {}
    if (!excludeCards && event.requirementsJson) {
      for (const card of event.requirementsJson) {
        if (!card.cardType || !card.cardType.title) {
          continue
        }
        const key = formatCardKey(card.cardType.title)
        if (!cards[key]) { cards[key] = [] }
        cards[key].push(card.description)
      }
    }

    const roleName = svgLanes[event.laneId]?.name || 'Unknown Role'

    const stepData = {
      step: index,
      role: roleName,
      description: event.name || 'Unnamed Event',
      ...cards
    }

    if (previousEvent?.type === 'bpmn:ExclusiveGateway') {
      const conditionIndex = event.conditionLabels?.findIndex(label => label.parentId === previousEvent.id)
      if (conditionIndex > -1 && res.length > 0) {
        res[res.length - 1].description += ` : ${event.conditionLabels[conditionIndex].name}`
      }
    }

    res.push(stepData)

    if (event.id === effectiveStartEventId) {
      currentStep = index
    }
    previousEvent = event
    index++
  }

  return { workflow: res, currentStep }
}

export const getCardTypeMetadata = (cardTypes) => {
  const content = []

  if (!cardTypes || !Array.isArray(cardTypes)) {
    return ''
  }

  cardTypes.forEach((cardType) => {
    if (cardType.generateWithAI) {
      content.push(`${formatCardKey(cardType.title)} : ${cardType.description}`)
    }
  })

  return content
}

export const getWorkflowBackgroundSections = (workflow, currentDiagram, cardTypes) => {
  const promptSections = []

  promptSections.push({
    title: 'Background - Workflow Description (business process, workflow or project scope)',
    content: workflow.aiDescription || 'N/A'
  })

  promptSections.push({
    title: 'Background workflow (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify(currentDiagram)
  })

  promptSections.push({
    title: 'Clarification',
    content: [
      `Each step in the workflow JSON contains the step number, the role and the description.
       Each step (in the workflow) can also include metadata with the following meanings:`,
      ...getCardTypeMetadata(cardTypes)
    ]
  })

  return promptSections
}

export const getEventDataModelsBackground = (event, currentStepObject, cardTypes) => {
  let writeModelBackground = null
  let readModelBackground = null
  const roleName = currentStepObject?.role
  const description = currentStepObject?.description
  const writeModel = event?.dataModels?.find((model) => model.type === 'Event Story')
  const readModel = event?.dataModels?.find((model) => model.type === 'Read Model')
  const aggregateName = getAggregateName(currentStepObject, cardTypes)
  const commandName = getCommandName(currentStepObject, cardTypes)

  const writeModelDataFields = writeModel ? writeModel.dataFields.map(({ name }) => ({ name })) || [] : null
  const readModelDataFields = readModel ? readModel.dataFields.map(({ name }) => ({ name })) || [] : null

  if (writeModelDataFields) {
    writeModelBackground = {
      description: `Below is a JSON object representing the input form (UX wireframe or mockup) for
        step ${currentStepObject.step}, ${roleName} - ${description}.`,
      data: writeModelDataFields
    }

    if (commandName && aggregateName) {
      writeModelBackground.description +=
        ` (The command is "${commandName}" and it's invoked on the aggregate root "${aggregateName}".)`
    } else {
      writeModelBackground.description +=
        ` (The command that is invoked in the step ${currentStepObject.step}: ${roleName} - ${description}.)`
    }
  }

  if (readModelDataFields) {
    readModelBackground = {
      description: `Below is a JSON object describing the read model for step ${currentStepObject.step},
        ${roleName} - ${description} view, which provides the key attributes and their context.`,
      data: readModelDataFields
    }
  }

  return [writeModelBackground, readModelBackground]
}

export const getEventsDataModelsBackgroundSections = (events, currentDiagram, cardTypes) => {
  if (!events?.length) {
    return []
  }

  const promptSections = []
  const writeModelsBackground = []
  const readModelsBackground = []

  events.forEach((event, index) => {
    const currentStepObject = currentDiagram[index]
    const [writeModelInfo, readModelInfo] = getEventDataModelsBackground(event, currentStepObject, cardTypes)

    if (writeModelInfo) {
      writeModelsBackground.push(writeModelInfo)
    }
    if (readModelInfo) {
      readModelsBackground.push(readModelInfo)
    }
  })

  if (writeModelsBackground.length) {
    promptSections.push({
      title: 'Background on the Input Forms',
      spacingAfter: 1,
      content: `
        Each step of the process should be seen as an actor (human or system) invoking a command on an aggregate.
        In order to perform that command, parameters (or arguments) are needed.
      `
    })

    writeModelsBackground.forEach((item, index) => {
      promptSections.push({ content: item.description, spacingAfter: 0 })
      promptSections.push({
        contentType: 'codeblock',
        language: 'json',
        content: JSON.stringify(item.data),
        spacingAfter: index === writeModelsBackground.length - 1 ? 2 : 1
      })
    })
  }

  if (readModelsBackground.length) {
    promptSections.push({
      title: 'Background on the Read Models',
      spacingAfter: 1,
      content: `
        The Read Model represents the projection of data that is optimized for querying and displaying
        information to users or systems. It is designed to provide a denormalized, query-friendly view
        of the data, derived from one or more events or aggregates. Each field in the read model corresponds
        to a piece of information required to fulfill a specific query or display need.
      `
    })

    readModelsBackground.forEach((item, index) => {
      promptSections.push({ content: item.description, spacingAfter: 0 })
      promptSections.push({
        contentType: 'codeblock',
        language: 'json',
        content: JSON.stringify(item.data),
        spacingAfter: index === readModelsBackground.length - 1 ? 2 : 1
      })
    })
  }

  return promptSections
}

export const getRoleCommandAggregateBackground = ({ currentStep, roleName, description, commandName, aggregateName }) => {
  let content = ''

  content += `The domain event in the workflow that we are working with right now is the event: ${currentStep}: ${roleName} - ${description}. `
  if (commandName && aggregateName) {
    content += `This step involves the role (or actor) "${roleName}" invoking the command "${commandName}" on the aggregate "${aggregateName}".`
  } else {
    content += `This step involves the role (or actor) "${roleName}" invoking a command on an aggregate (create, update or delete).`
  }
  return content
}
